import { useState, useEffect, useMemo } from "react";
import { getTaxYearLabel } from "@/utils/dateUtils";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Plus, FileText, Download, Mail, Eye, Settings, Trash2, AlertTriangle, Bell, Car, Upload } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiClient, API } from "@/App";
import { toast } from "sonner";
import { format, parseISO, isPast } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Loader2, Send } from "lucide-react";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

// ───── Stat tile (used in the header row of /invoices) ─────────────────
function StatTile({ label, value, valueColor }) {
  return (
    <div
      style={{
        background: tokens.color.cardBg,
        border: tokens.border.card,
        borderRadius: tokens.radius.lg,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
      }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: tokens.font.mono,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: tokens.color.textMuted,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          marginTop: 4,
          fontFamily: tokens.font.body,
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: valueColor || tokens.color.text,
        }}
      >
        {value}
      </p>
    </div>
  );
}

export const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  // FreeAgent push state
  const [freeagentConnected, setFreeagentConnected] = useState(false);
  const [pushingInvoiceId, setPushingInvoiceId] = useState(null);
  // Xero push state
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroPushingInvoiceId, setXeroPushingInvoiceId] = useState(null);
  // QuickBooks push state
  const [quickbooksConnected, setQuickbooksConnected] = useState(false);
  const [quickbooksPushingInvoiceId, setQuickbooksPushingInvoiceId] = useState(null);
  // Money hub: Mileage tab state (HMRC mileage claims, surfaced alongside
  // invoices now that everything financial lives at /money).
  const [activeTab, setActiveTab] = useState("invoices");
  const [mileageClaims, setMileageClaims] = useState([]);
  // Mileage filters — two dropdowns. The tax-year dropdown bakes in
  // presets ('this tax year', 'last tax year', 'last 3 months') alongside
  // explicit historical years; the month dropdown narrows within them.
  const [filterTaxYear, setFilterTaxYear] = useState("all"); // "all" | "current" | "previous" | "last3mo" | "YYYY/YY"
  const [filterMonth, setFilterMonth] = useState("all");     // "all" | "01" ... "12"

  // Email preview modal state. When `emailModal.open` is true, the Dialog
  // renders with editable to/subject/message fields pre-filled from the
  // /email-data backend endpoint. `attachmentFilename` is display-only —
  // the PDF is generated server-side.
  const [emailModal, setEmailModal] = useState({
    open: false,
    invoiceId: null,
    invoiceNumber: "",
    attachmentFilename: "",
    to: "",
    subject: "",
    message: "",
    loading: false,
    sending: false,
  });

  useEffect(() => {
    fetchInvoices();
    fetchMileageClaims();
    apiClient.get("/accounting/freeagent/status")
      .then(r => setFreeagentConnected(!!r.data?.connected))
      .catch(() => setFreeagentConnected(false));
    apiClient.get("/accounting/xero/status")
      .then(r => setXeroConnected(!!r.data?.connected))
      .catch(() => setXeroConnected(false));
    apiClient.get("/accounting/quickbooks/status")
      .then(r => setQuickbooksConnected(!!r.data?.connected))
      .catch(() => setQuickbooksConnected(false));
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await apiClient.get("/invoices");
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  // ── Mileage ─────────────────────────────────────────────────────────
  const fetchMileageClaims = async () => {
    try {
      const res = await apiClient.get("/mileage-claims");
      setMileageClaims(res.data || []);
    } catch (e) {
      console.error("Failed to load mileage claims:", e);
    }
  };

  const deleteMileageClaim = async (claimId) => {
    try {
      await apiClient.delete(`/mileage-claims/${claimId}`);
      setMileageClaims(prev => prev.filter(c => c.claim_id !== claimId));
    } catch (e) {
      console.error("Failed to delete mileage claim:", e);
    }
  };

  const currentTaxYear = useMemo(() => getTaxYearLabel(new Date().toISOString()), []);
  const previousTaxYear = useMemo(() => {
    if (!currentTaxYear) return null;
    const start = parseInt(currentTaxYear.split("/")[0], 10) - 1;
    return `${start}/${String(start + 1).slice(-2)}`;
  }, [currentTaxYear]);

  const availableTaxYears = useMemo(() => {
    const set = new Set(mileageClaims.map(c => getTaxYearLabel(c.date)).filter(Boolean));
    return Array.from(set).sort().reverse(); // newest first
  }, [mileageClaims]);

  // 'Last 3 months' is a rolling window; in that mode the month filter
  // doesn't apply (a custom window doesn't have a single month).
  const isRolling = filterTaxYear === "last3mo";

  const filteredMileage = useMemo(() => {
    return mileageClaims.filter(c => {
      if (!c.date) return false;
      const d = new Date(c.date);
      if (isNaN(d)) return false;

      if (filterTaxYear === "last3mo") {
        const cutoff = new Date();
        cutoff.setMonth(cutoff.getMonth() - 3);
        return d >= cutoff;
      }

      if (filterTaxYear !== "all") {
        const target = filterTaxYear === "current" ? currentTaxYear
                     : filterTaxYear === "previous" ? previousTaxYear
                     : filterTaxYear;
        if (getTaxYearLabel(c.date) !== target) return false;
      }

      if (filterMonth !== "all") {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        if (mm !== filterMonth) return false;
      }

      return true;
    });
  }, [mileageClaims, filterTaxYear, filterMonth, currentTaxYear, previousTaxYear]);

  const phaseLabel = (phase) => ({
    round_trip: "Round trip",
    outbound: "Home → venue",
    return: "Venue → home",
    between: "Between venues",
    airport_round_trip: "Round trip (airport)",
    airport_outbound: "Home → airport",
    airport_return: "Airport → home",
  }[phase] || "Round trip");

  const exportMileageCsv = () => {
    // Filename reflects active filter so downloads are self-labelling.
    let suffix = "all";
    if (filterTaxYear === "last3mo") suffix = "last-3-months";
    else if (filterTaxYear !== "all" || filterMonth !== "all") {
      const tyLabel = filterTaxYear === "current" ? currentTaxYear
                    : filterTaxYear === "previous" ? previousTaxYear
                    : filterTaxYear === "all" ? null : filterTaxYear;
      const monthLabel = filterMonth === "all" ? null
                       : new Date(2000, parseInt(filterMonth, 10) - 1, 1).toLocaleString("en-GB", { month: "long" }).toLowerCase();
      suffix = [tyLabel && `TY${tyLabel.replace("/", "-")}`, monthLabel].filter(Boolean).join("-");
    }

    const rows = [
      ["Date", "Artist / Band", "Location", "Journey", "Miles", "Amount (£)"],
      ...filteredMileage.map(c => [
        c.date || "",
        c.band_name || "",
        [c.city, c.country].filter(Boolean).join(", "),
        c.phase_label || phaseLabel(c.phase),
        c.miles,
        c.amount_gbp.toFixed(2),
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mileage-${suffix || "log"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;

    try {
      await apiClient.delete(`/invoices/${invoiceToDelete}`);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const handlePushToFreeagent = async (invoiceId) => {
    setPushingInvoiceId(invoiceId);
    try {
      const { data } = await apiClient.post(`/invoices/${invoiceId}/push-to-freeagent`);
      toast.success(data.message || "Pushed to FreeAgent", {
        action: data.freeagent_invoice_url ? {
          label: "Open",
          onClick: () => window.open(data.freeagent_invoice_url, "_blank"),
        } : undefined,
      });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to push to FreeAgent");
    } finally {
      setPushingInvoiceId(null);
    }
  };

  const handlePushToXero = async (invoiceId) => {
    setXeroPushingInvoiceId(invoiceId);
    try {
      const { data } = await apiClient.post(`/invoices/${invoiceId}/push-to-xero`);
      toast.success(data.message || "Pushed to Xero", {
        action: data.xero_invoice_url ? {
          label: "Open",
          onClick: () => window.open(data.xero_invoice_url, "_blank"),
        } : undefined,
      });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to push to Xero");
    } finally {
      setXeroPushingInvoiceId(null);
    }
  };

  const handlePushToQuickbooks = async (invoiceId) => {
    setQuickbooksPushingInvoiceId(invoiceId);
    try {
      const { data } = await apiClient.post(`/invoices/${invoiceId}/push-to-quickbooks`);
      toast.success(data.message || "Pushed to QuickBooks", {
        action: data.quickbooks_invoice_url ? {
          label: "Open",
          onClick: () => window.open(data.quickbooks_invoice_url, "_blank"),
        } : undefined,
      });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to push to QuickBooks");
    } finally {
      setQuickbooksPushingInvoiceId(null);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(link);
      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          try { document.body.removeChild(link); } catch {}
          window.URL.revokeObjectURL(url);
        }, 500);
      });
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleEmail = async (invoiceId) => {
    // Open the preview modal and hydrate to/subject/message from the existing
    // /email-data endpoint (reused so copy stays consistent with other flows).
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);
    const invoiceNumber = invoice?.invoice_number || "";
    setEmailModal(s => ({
      ...s,
      open: true,
      invoiceId,
      invoiceNumber,
      attachmentFilename: `Invoice_${(invoiceNumber || "").replace(/\s+/g, "_")}.pdf`,
      to: "",
      subject: "",
      message: "",
      loading: true,
      sending: false,
    }));
    try {
      const res = await apiClient.get(`/invoices/${invoiceId}/email-data`);
      setEmailModal(s => ({
        ...s,
        loading: false,
        to: res.data?.to || invoice?.recipient_email || "",
        subject: res.data?.subject || `Invoice ${invoiceNumber}`,
        message: res.data?.body || "",
      }));
    } catch (error) {
      setEmailModal(s => ({
        ...s,
        loading: false,
        to: invoice?.recipient_email || "",
        subject: `Invoice ${invoiceNumber}`,
        message: "",
      }));
      toast.error("Failed to prepare email preview");
    }
  };

  const handleSendInvoiceEmail = async () => {
    const { invoiceId, to, subject, message } = emailModal;
    if (!to.trim() || !subject.trim() || !message.trim()) {
      toast.error("To, subject and message are required");
      return;
    }
    setEmailModal(s => ({ ...s, sending: true }));
    try {
      await apiClient.post(`/invoices/${invoiceId}/email`, {
        to: to.trim(),
        subject: subject.trim(),
        message,
      });
      toast.success("Invoice sent successfully");
      setEmailModal(s => ({ ...s, open: false, sending: false }));
      fetchInvoices();  // Refresh so the draft→sent status transition is reflected.
    } catch (error) {
      setEmailModal(s => ({ ...s, sending: false }));
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/send-reminder`);
      toast.success(`Reminder #${response.data.reminder_number} ready (${response.data.tone} tone)`);
      window.location.href = response.data.mailto_link;
      fetchInvoices();  // Refresh to show updated reminder count
    } catch (error) {
      if (error.response?.data?.detail === "Invoice is already paid") {
        toast.info("Invoice is already paid");
      } else {
        toast.error("Failed to prepare reminder");
      }
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      const { data } = await apiClient.put(`/invoices/${invoiceId}`, { status: "paid" });
      const sync = data?._accounting_sync;
      if (sync && Object.keys(sync).length > 0) {
        const label = (p) => (p === "freeagent" ? "FreeAgent" : p === "quickbooks" ? "QuickBooks" : "Xero");
        const synced = Object.entries(sync).filter(([, r]) => r.ok).map(([p]) => label(p));
        const failed = Object.entries(sync).filter(([, r]) => !r.ok).map(([p, r]) => `${label(p)}: ${r.error}`);
        if (synced.length) toast.success(`Marked paid · payment recorded in ${synced.join(" & ")}`);
        else toast.success("Invoice marked as paid");
        if (failed.length) toast.error(`Couldn't record payment — ${failed.join(" · ")}`);
      } else {
        toast.success("Invoice marked as paid");
      }
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filterStatus === "all") return true;
    return inv.status === filterStatus;
  });

  // Token-driven invoice-status pill. Red/amber/green/blue/grey pairs
  // at low-opacity backgrounds, mono caps 11px label — matches the gig
  // status pill recipe used elsewhere.
  const StatusPill = ({ status, lineThrough }) => {
    const visual = {
      draft:     { bg: "rgba(107, 105, 99, 0.18)", color: tokens.color.textMuted, label: "Draft" },
      sent:      { bg: "rgba(96, 165, 250, 0.16)", color: "#60a5fa",              label: "Sent" },
      paid:      { bg: "rgba(34, 193, 151, 0.14)", color: tokens.color.green,     label: "Paid" },
      overdue:   { bg: "rgba(239, 68, 68, 0.14)",  color: tokens.color.red,       label: "Overdue" },
      cancelled: { bg: "rgba(107, 105, 99, 0.18)", color: tokens.color.textMuted, label: "Cancelled" },
    }[status] || { bg: "rgba(107, 105, 99, 0.18)", color: tokens.color.textMuted, label: status };

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 8px",
          borderRadius: tokens.radius.sm,
          background: visual.bg,
          color: visual.color,
          fontFamily: tokens.font.mono,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          textDecoration: lineThrough ? "line-through" : "none",
          whiteSpace: "nowrap",
        }}
      >
        {visual.label}
      </span>
    );
  };

  const getStatusBadge = (invoice) => {
    const status = invoice.status;
    const dueDate = parseISO(invoice.due_date);
    const isOverdue = status !== "paid" && status !== "cancelled" && isPast(dueDate);
    if (isOverdue && status !== "overdue") return <StatusPill status="overdue" />;
    if (status === "cancelled") return <StatusPill status="cancelled" lineThrough />;
    return <StatusPill status={status || "draft"} />;
  };

  // Calculate stats
  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === "draft").length,
    sent: invoices.filter(i => i.status === "sent").length,
    paid: invoices.filter(i => i.status === "paid").length,
    totalValue: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paidValue: invoices.filter(i => i.status === "paid").reduce((sum, i) => sum + (i.total || 0), 0),
    outstandingValue: invoices.filter(i => i.status !== "paid" && i.status !== "cancelled").reduce((sum, i) => sum + (i.total || 0), 0),
  };

  return (
    <Layout>
      <div data-testid="money-page">
        {/* Header — Money hub. Kicker MONEY, h1 'Your Money'. Tabs below
            host Invoices and Mileage (more sections planned). */}
        <div style={{ marginBottom: tokens.space.xxl }}>
          <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
            Money
          </Kicker>
          <div
            className="flex flex-row items-center justify-between"
            style={{ gap: tokens.space.md }}
          >
            <h1
              style={{
                margin: 0,
                ...tokens.type.heroSm,
                color: tokens.color.text,
                textWrap: "balance",
                minWidth: 0,
              }}
            >
              Your Money
            </h1>
            <div style={{ display: "flex", gap: tokens.space.sm, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => navigate("/invoices/settings")}
                data-testid="invoice-settings-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "10px 14px",
                  background: "transparent",
                  color: tokens.color.textDim,
                  border: tokens.border.card,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
              <button
                type="button"
                onClick={() => navigate("/invoices/new")}
                data-testid="new-invoice-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "10px 16px",
                  background: tokens.color.orange,
                  color: tokens.color.onOrange,
                  border: tokens.border.brand,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <Plus className="h-4 w-4" />
                New Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Tabs: Invoices | Mileage */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            className="grid grid-cols-2 w-full md:w-[280px]"
            style={{ marginBottom: tokens.space.xl, flexShrink: 0 }}
          >
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="invoices-tab">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="mileage" className="flex items-center gap-2" data-testid="mileage-tab">
              <Car className="h-4 w-4" />
              Mileage
            </TabsTrigger>
          </TabsList>

          <TabsContent value="invoices">
        {/* Stats Cards — token-driven mini tiles */}
        <div
          className="grid grid-cols-2 md:grid-cols-4"
          style={{ gap: tokens.space.md, marginBottom: tokens.space.xl }}
        >
          <StatTile label="Total Invoiced" value={`£${stats.totalValue.toFixed(2)}`} />
          <StatTile label="Paid" value={`£${stats.paidValue.toFixed(2)}`} valueColor={tokens.color.green} />
          <StatTile label="Outstanding" value={`£${stats.outstandingValue.toFixed(2)}`} valueColor={tokens.color.amber} />
          <StatTile label="Invoices" value={stats.total} />
        </div>

        {/* Filter */}
        <div
          className="flex justify-between items-center"
          style={{ marginBottom: tokens.space.lg }}
        >
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="filter-status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Invoices</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoice Table */}
        <div
          style={{
            background: tokens.color.cardBg,
            border: tokens.border.card,
            borderRadius: tokens.radius.lg,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: `${tokens.space.xxl}px ${tokens.space.xl}px`,
                textAlign: "center",
                ...tokens.type.bodySm,
                color: tokens.color.textMuted,
              }}
            >
              Loading invoices…
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div
              style={{
                padding: `${tokens.space.xxxl}px ${tokens.space.xl}px`,
                textAlign: "center",
              }}
            >
              <FileText
                style={{
                  width: 48,
                  height: 48,
                  color: tokens.color.textMuted,
                  margin: "0 auto",
                  marginBottom: tokens.space.md,
                }}
              />
              <p
                style={{
                  margin: 0,
                  marginBottom: tokens.space.lg,
                  ...tokens.type.body,
                  color: tokens.color.textMuted,
                }}
              >
                No invoices yet
              </p>
              <button
                type="button"
                onClick={() => navigate("/invoices/new")}
                data-testid="create-first-invoice"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "10px 16px",
                  background: tokens.color.orange,
                  color: tokens.color.onOrange,
                  border: tokens.border.brand,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Plus className="h-4 w-4" />
                Create Your First Invoice
              </button>
            </div>
          ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const dueDate = parseISO(invoice.due_date);
                    const isOverdue = invoice.status !== "paid" && invoice.status !== "cancelled" && isPast(dueDate);
                    
                    return (
                    <TableRow 
                      key={invoice.invoice_id} 
                      className={`border-border cursor-pointer hover:bg-secondary/30 ${isOverdue ? 'bg-red-500/5' : ''}`}
                      onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                      data-testid={`invoice-row-${invoice.invoice_id}`}
                    >
                      <TableCell className="font-mono font-medium">
                        {invoice.invoice_number}
                        {invoice.reminder_count > 0 && (
                          <Badge variant="outline" className="ml-2 text-xs text-amber-400 border-amber-500/30">
                            {invoice.reminder_count} reminder{invoice.reminder_count > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{invoice.recipient_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(parseISO(invoice.issue_date), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell className={isOverdue ? "text-red-400 font-medium" : "text-muted-foreground"}>
                        {format(dueDate, 'dd MMM yyyy')}
                        {isOverdue && (
                          <AlertTriangle className="h-3 w-3 inline ml-1 text-red-400" />
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        £{invoice.total?.toFixed(2)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                          {freeagentConnected && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePushToFreeagent(invoice.invoice_id)}
                              disabled={pushingInvoiceId === invoice.invoice_id}
                              title={invoice.freeagent_invoice_url ? "Push again to FreeAgent" : "Push to FreeAgent"}
                              className={invoice.freeagent_invoice_url ? "text-emerald-400 hover:text-emerald-300" : ""}
                              data-testid={`push-freeagent-${invoice.invoice_id}`}
                            >
                              {pushingInvoiceId === invoice.invoice_id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Upload className="h-4 w-4" />}
                            </Button>
                          )}
                          {xeroConnected && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePushToXero(invoice.invoice_id)}
                              disabled={xeroPushingInvoiceId === invoice.invoice_id}
                              title={invoice.xero_invoice_url ? "Push again to Xero" : "Push to Xero"}
                              className={invoice.xero_invoice_url ? "text-sky-400 hover:text-sky-300" : ""}
                              data-testid={`push-xero-${invoice.invoice_id}`}
                            >
                              {xeroPushingInvoiceId === invoice.invoice_id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Upload className="h-4 w-4" />}
                            </Button>
                          )}
                          {quickbooksConnected && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePushToQuickbooks(invoice.invoice_id)}
                              disabled={quickbooksPushingInvoiceId === invoice.invoice_id}
                              title={invoice.qbo_invoice_url ? "Push again to QuickBooks" : "Push to QuickBooks"}
                              className={invoice.qbo_invoice_url ? "text-green-400 hover:text-green-300" : ""}
                              data-testid={`push-quickbooks-${invoice.invoice_id}`}
                            >
                              {quickbooksPushingInvoiceId === invoice.invoice_id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Upload className="h-4 w-4" />}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadPdf(invoice.invoice_id)}
                            title="Download PDF"
                            data-testid={`download-pdf-${invoice.invoice_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEmail(invoice.invoice_id)}
                            title="Send Email"
                            data-testid={`email-${invoice.invoice_id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          {isOverdue && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSendReminder(invoice.invoice_id)}
                              title={`Send Reminder #${(invoice.reminder_count || 0) + 1}`}
                              className="text-amber-400 hover:text-amber-300"
                              data-testid={`reminder-${invoice.invoice_id}`}
                            >
                              <Bell className="h-4 w-4" />
                            </Button>
                          )}
                          {invoice.status !== "paid" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkPaid(invoice.invoice_id)}
                              className="text-emerald-400 hover:text-emerald-300"
                              data-testid={`mark-paid-${invoice.invoice_id}`}
                            >
                              Mark Paid
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setInvoiceToDelete(invoice.invoice_id);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-${invoice.invoice_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          )}
        </div>
          </TabsContent>

          <TabsContent value="mileage">
            {/* Mileage tab — HMRC mileage claims, ported from Stats. */}
            <div
              style={{
                background: tokens.color.cardBg,
                border: tokens.border.card,
                borderRadius: tokens.radius.lg,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: tokens.space.md,
                  padding: tokens.space.xl,
                  borderBottom: tokens.border.hairline,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h2
                    style={{
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      ...tokens.type.titleSm,
                      color: tokens.color.text,
                    }}
                  >
                    <Car className="h-4 w-4" />
                    Mileage Log
                  </h2>
                  <p
                    style={{
                      margin: 0,
                      marginTop: 4,
                      ...tokens.type.bodySm,
                      color: tokens.color.textMuted,
                    }}
                  >
                    HMRC mileage claims — logged when you add mileage to a gig's expenses.
                  </p>
                </div>
                {mileageClaims.length > 0 && (
                  <Button variant="outline" size="sm" onClick={exportMileageCsv} className="shrink-0" data-testid="mileage-export-csv">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
                  </Button>
                )}
              </div>

              {mileageClaims.length === 0 ? (
                <div style={{ padding: `${tokens.space.xxxl}px ${tokens.space.xl}px`, textAlign: "center" }}>
                  <Car style={{ width: 48, height: 48, color: tokens.color.textMuted, margin: "0 auto", marginBottom: tokens.space.md }} />
                  <p style={{ margin: 0, ...tokens.type.body, color: tokens.color.textMuted }}>
                    No mileage claims yet — use the mileage calculator on a gig set to Driving transport.
                  </p>
                </div>
              ) : (
                <div style={{ padding: tokens.space.xl }}>
                  {/* Filter toolbar — two dropdowns. Tax year bakes in presets
                      (this/last tax year, last 3 months) above the historical
                      list. Month narrows within the selected tax year; ignored
                      when 'Last 3 months' is active. */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: tokens.space.sm,
                      marginBottom: tokens.space.lg,
                    }}
                  >
                    <Select
                      value={filterTaxYear}
                      onValueChange={(v) => {
                        setFilterTaxYear(v);
                        // Auto-clear month when switching into the rolling window
                        if (v === "last3mo") setFilterMonth("all");
                      }}
                    >
                      <SelectTrigger className="w-[200px]" data-testid="mileage-filter-tax-year">
                        <SelectValue placeholder="Tax year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All time</SelectItem>
                        <SelectItem value="current">This tax year{currentTaxYear ? ` (${currentTaxYear})` : ""}</SelectItem>
                        <SelectItem value="previous">Last tax year{previousTaxYear ? ` (${previousTaxYear})` : ""}</SelectItem>
                        <SelectItem value="last3mo">Last 3 months</SelectItem>
                        {availableTaxYears.length > 0 && (
                          <>
                            {/* Divider before the explicit historical list */}
                            <div
                              aria-hidden="true"
                              style={{
                                height: 1,
                                margin: `${tokens.space.xs}px 0`,
                                background: tokens.color.divider,
                              }}
                            />
                            {availableTaxYears.map(ty => (
                              <SelectItem key={ty} value={ty}>{ty}</SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <Select
                      value={filterMonth}
                      onValueChange={setFilterMonth}
                      disabled={isRolling}
                    >
                      <SelectTrigger
                        className="w-[160px]"
                        data-testid="mileage-filter-month"
                        title={isRolling ? "Month filter doesn't apply to 'Last 3 months'" : undefined}
                      >
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All months</SelectItem>
                        {[
                          ["01","January"],["02","February"],["03","March"],
                          ["04","April"],["05","May"],["06","June"],
                          ["07","July"],["08","August"],["09","September"],
                          ["10","October"],["11","November"],["12","December"],
                        ].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Totals row — reflect the filtered set */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: tokens.space.xl,
                      marginBottom: tokens.space.lg,
                      padding: tokens.space.md,
                      borderRadius: tokens.radius.md,
                      background: tokens.color.innerBg,
                      border: tokens.border.inner,
                      ...tokens.type.bodySm,
                    }}
                  >
                    <div>
                      <span style={{ color: tokens.color.textMuted }}>Total miles </span>
                      <span style={{ fontWeight: 600, color: tokens.color.text }}>
                        {filteredMileage.reduce((s, c) => s + c.miles, 0).toFixed(1)}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: tokens.color.textMuted }}>Total claimed </span>
                      <span style={{ fontWeight: 600, color: tokens.color.green }}>
                        £{filteredMileage.reduce((s, c) => s + c.amount_gbp, 0).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: tokens.color.textMuted }}>Claims </span>
                      <span style={{ fontWeight: 600, color: tokens.color.text }}>{filteredMileage.length}</span>
                      {filteredMileage.length !== mileageClaims.length && (
                        <span style={{ color: tokens.color.textMuted }}> of {mileageClaims.length}</span>
                      )}
                    </div>
                  </div>

                  {filteredMileage.length === 0 ? (
                    <p style={{ ...tokens.type.bodySm, color: tokens.color.textMuted, margin: 0, padding: tokens.space.lg, textAlign: "center" }}>
                      No claims match these filters.
                    </p>
                  ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="mileage-table">
                      <thead>
                        <tr style={{ borderBottom: tokens.border.hairline }}>
                          <th className="text-left py-2 font-medium" style={{ color: tokens.color.textDim }}>Date</th>
                          <th className="text-left py-2 font-medium" style={{ color: tokens.color.textDim }}>Artist / Band</th>
                          <th className="text-left py-2 font-medium" style={{ color: tokens.color.textDim }}>Location</th>
                          <th className="text-left py-2 font-medium" style={{ color: tokens.color.textDim }}>Journey</th>
                          <th className="text-right py-2 font-medium" style={{ color: tokens.color.textDim }}>Miles</th>
                          <th className="text-right py-2 font-medium" style={{ color: tokens.color.textDim }}>Amount</th>
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMileage.map(claim => (
                          <tr key={claim.claim_id} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                            <td className="py-2" style={{ color: tokens.color.textMuted }}>
                              {claim.date ? format(parseISO(claim.date), "d MMM yyyy") : "—"}
                            </td>
                            <td className="py-2" style={{ color: tokens.color.text }}>{claim.band_name || "—"}</td>
                            <td className="py-2" style={{ color: tokens.color.textMuted }}>
                              {[claim.city, claim.country].filter(Boolean).join(", ") || "—"}
                            </td>
                            <td className="py-2" style={{ color: tokens.color.textMuted }}>{claim.phase_label || phaseLabel(claim.phase)}</td>
                            <td className="py-2 text-right" style={{ color: tokens.color.text }}>{claim.miles}</td>
                            <td className="py-2 text-right" style={{ color: tokens.color.green }}>£{claim.amount_gbp.toFixed(2)}</td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => deleteMileageClaim(claim.claim_id)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: tokens.color.textMuted,
                                  cursor: "pointer",
                                  padding: 4,
                                  transition: `color ${tokens.motion.fast}`,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = tokens.color.red)}
                                onMouseLeave={(e) => (e.currentTarget.style.color = tokens.color.textMuted)}
                                title="Delete claim"
                                data-testid={`delete-mileage-${claim.claim_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this invoice? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Invoice Email Preview Modal ──
            Editable to/subject/message pre-filled from /email-data. The PDF is
            generated server-side on submit — the filename shown here is a
            non-removable indicator matching what the backend will attach. */}
        <Dialog
          open={emailModal.open}
          onOpenChange={(isOpen) => {
            if (!emailModal.sending) setEmailModal(s => ({ ...s, open: isOpen }));
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-email-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email invoice
                {emailModal.invoiceNumber && (
                  <span className="text-muted-foreground text-sm font-normal">
                    · {emailModal.invoiceNumber}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                Review the message before sending. The PDF will be attached automatically.
              </DialogDescription>
            </DialogHeader>

            {emailModal.loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="invoice-email-to">To</Label>
                  <Input
                    id="invoice-email-to"
                    type="email"
                    value={emailModal.to}
                    onChange={(e) => setEmailModal(s => ({ ...s, to: e.target.value }))}
                    data-testid="invoice-email-to"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invoice-email-subject">Subject</Label>
                  <Input
                    id="invoice-email-subject"
                    value={emailModal.subject}
                    onChange={(e) => setEmailModal(s => ({ ...s, subject: e.target.value }))}
                    data-testid="invoice-email-subject"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invoice-email-message">Message</Label>
                  <Textarea
                    id="invoice-email-message"
                    value={emailModal.message}
                    onChange={(e) => setEmailModal(s => ({ ...s, message: e.target.value }))}
                    rows={10}
                    className="font-mono text-sm"
                    data-testid="invoice-email-message"
                  />
                </div>
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary/40 border border-border text-sm"
                  data-testid="invoice-email-attachment"
                >
                  <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{emailModal.attachmentFilename}</span>
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">PDF · auto-attached</span>
                </div>
              </div>
            )}

            <DialogFooter className="mt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailModal(s => ({ ...s, open: false }))}
                disabled={emailModal.sending}
                data-testid="invoice-email-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSendInvoiceEmail}
                disabled={emailModal.loading || emailModal.sending}
                data-testid="invoice-email-send"
              >
                {emailModal.sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InvoiceList;
